/**
 * Servicio para interactuar con los endpoints de envío (Correo Argentino / MiCorreo).
 *
 * Endpoints backend:
 *   GET/PUT  /api/shipping/config/           → Configuración (admin)
 *   GET      /api/shipping/config/public/    → Config pública (habilitado + tipos entrega)
 *   POST     /api/shipping/config/test/      → Test conexión (admin)
 *   POST     /api/shipping/rates/             → Cotizar envío
 *   POST     /api/shipping/import/           → Importar envío (admin)
 *   GET      /api/shipping/shipments/        → Listar envíos (admin)
 *   GET      /api/shipping/shipments/:id/tracking/ → Tracking
 *   GET      /api/shipping/orders/:id/shipment/    → Envío de una orden
 */
import apiClient from './apiClient';

// ─── Configuración pública ───

/**
 * Obtener si Correo Argentino está habilitado y tipos de entrega disponibles.
 * No requiere autenticación.
 */
export const getShippingPublicConfig = async () => {
  try {
    const response = await apiClient.get('/shipping/config/public/');
    return response.data;
  } catch (error) {
    console.error('Error fetching shipping public config:', error);
    return { is_enabled: false, delivery_to_door_enabled: false, delivery_to_branch_enabled: false };
  }
};

// ─── Configuración admin ───

export const getShippingConfig = async () => {
  const response = await apiClient.get('/shipping/config/');
  return response.data;
};

export const updateShippingConfig = async (configData) => {
  const response = await apiClient.put('/shipping/config/', configData);
  return response.data;
};

export const testShippingConnection = async () => {
  const response = await apiClient.post('/shipping/config/test/');
  return response.data;
};

/**
 * Obtener estado de la modalidad logística configurada (admin).
 */
export const getShippingModalidad = async () => {
  const response = await apiClient.get('/shipping/modalidad/');
  return response.data;
};

// ─── Cotización ───

/**
 * Cotizar envío con Correo Argentino.
 * @param {string} postalCode - Código postal del destinatario
 * @param {object} options - { weight, length, width, height, declared_value } (opcionales)
 */
export const getShippingRates = async (postalCode, options = {}) => {
  const response = await apiClient.post('/shipping/rates/', {
    postal_code: postalCode,
    ...options,
  });
  return response.data;
};

// ─── Importación (admin) ───

export const importShipment = async (shipmentData) => {
  const response = await apiClient.post('/shipping/import/', shipmentData);
  return response.data;
};

export const importExistingShipment = async (shipmentId, updates = {}) => {
  const response = await apiClient.post(`/shipping/shipments/${shipmentId}/import/`, updates);
  return response.data;
};

/** Vista previa admin: líneas, paquete calculado y datos del envío antes de importar. */
export const getShipmentImportPreview = async (shipmentId) => {
  const response = await apiClient.get(`/shipping/shipments/${shipmentId}/import-preview/`);
  return response.data;
};

export const updateShipment = async (shipmentId, data) => {
  const response = await apiClient.patch(`/shipping/shipments/${shipmentId}/update/`, data);
  return response.data;
};

// ─── Listado de envíos (admin) ───

/**
 * El backend puede responder como DRF paginado, { shipments, counts }, o anidar en `data`.
 * @param {unknown} raw
 * @returns {Array}
 */
export const extractShipmentListFromResponse = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'object') return [];

  const fromObject = (obj) => {
    if (!obj || typeof obj !== 'object') return null;
    const direct =
      obj.shipments ?? obj.results ?? obj.items ?? obj.list ?? obj.objects;
    if (Array.isArray(direct)) return direct;
    if (direct && typeof direct === 'object' && !Array.isArray(direct)) {
      const inner = fromObject(direct);
      if (inner) return inner;
    }
    if (obj.data !== undefined) {
      if (Array.isArray(obj.data)) return obj.data;
      if (obj.data && typeof obj.data === 'object') {
        const nested = fromObject(obj.data);
        if (nested) return nested;
      }
    }
    return null;
  };

  let list = fromObject(raw);
  if (list) return list;

  for (const key of Object.keys(raw)) {
    if (key === 'counts' || key === 'meta' || key === 'count' || key === 'next' || key === 'previous') continue;
    const v = raw[key];
    if (Array.isArray(v) && (v.length === 0 || (v[0] && typeof v[0] === 'object'))) {
      return v;
    }
  }
  return [];
};

export const getShipments = async (filters = {}) => {
  const params = { page: 1 };
  if (typeof filters.page === 'number') params.page = filters.page;
  if (filters.page_size != null) params.page_size = filters.page_size;
  else if (filters.limit != null) params.page_size = filters.limit;
  else params.page_size = 200;

  if (filters.status) params.status = filters.status;
  if (filters.order_id) params.order_id = filters.order_id;
  if (filters.search) params.search = filters.search;
  if (filters.paid_orders_only) params.paid_orders_only = '1';

  const response = await apiClient.get('/shipping/shipments/', { params });
  const data = response.data;
  const list = extractShipmentListFromResponse(data);
  return {
    ...data,
    shipments: list,
    counts: data?.counts ?? data?.meta?.counts,
  };
};

export const syncPendingShipments = async () => {
  const response = await apiClient.post('/shipping/sync-pending/');
  return response.data;
};

// ─── Tracking ───

export const getShipmentTracking = async (shipmentId) => {
  const response = await apiClient.get(`/shipping/shipments/${shipmentId}/tracking/`);
  return response.data;
};

export const getOrderShipment = async (orderId) => {
  try {
    const response = await apiClient.get(`/shipping/orders/${orderId}/shipment/`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// ─── Sucursales / Agencies ───

export const getAgencies = async ({ postalCode = '', provinceCode = '' } = {}) => {
  const params = {};
  if (postalCode) params.postal_code = postalCode;
  if (provinceCode) params.province_code = provinceCode;

  const response = await apiClient.get('/shipping/agencies/', { params });
  return response.data;
};

// ─── Sucursal Preferida ───

export const getPreferredBranch = async () => {
  try {
    const response = await apiClient.get('/auth/preferred-branch/');
    return response.data;
  } catch (error) {
    console.error('Error fetching preferred branch:', error);
    return { preferred_agency_code: '', preferred_agency_name: '', has_preferred: false };
  }
};

export const savePreferredBranch = async (agencyCode, agencyName) => {
  const response = await apiClient.put('/auth/preferred-branch/', {
    agency_code: agencyCode,
    agency_name: agencyName,
  });
  return response.data;
};

export const deletePreferredBranch = async () => {
  const response = await apiClient.delete('/auth/preferred-branch/');
  return response.data;
};
